import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { TabsWrapper } from 'framework/ui/TabsWrapper';
import {
  CORE_SERVICE_STORAGE_PREFIX,
  CORE_SERVICE_TAB_PARAM,
  CORE_SERVICE_TABS,
  CORE_SERVICE_TITLE,
  CoreServiceTab,
  SYNC_STATUS_POLL_MS,
} from './constants';
import { useCoreServiceClient } from './api/coreServiceClient';
import { CoreEventsProvider } from './hooks/CoreEventsProvider';
import useAsyncData from './hooks/useAsyncData';
import AllocationTab from './tabs/Allocation/AllocationTab';
import BlueprintsTab from './tabs/Blueprints/BlueprintsTab';
import EventsTab from './tabs/Events/EventsTab';
import TodayTab from './tabs/Today/TodayTab';
import TreeTab from './tabs/Tree/TreeTab';
import type { SyncStatusDto } from './types';
import { formatTime, loadStoredValue, saveStoredValue } from './utils/format';

const TAB_STORAGE_KEY = `${CORE_SERVICE_STORAGE_PREFIX}.tab`;

const TAB_COMPONENTS: Record<CoreServiceTab, () => JSX.Element> = {
  [CoreServiceTab.TODAY]: TodayTab,
  [CoreServiceTab.BLUEPRINTS]: BlueprintsTab,
  [CoreServiceTab.TREE]: TreeTab,
  [CoreServiceTab.ALLOCATION]: AllocationTab,
  [CoreServiceTab.EVENTS]: EventsTab,
};

const isCoreServiceTab = (value: Nullable<string>): value is CoreServiceTab =>
  CORE_SERVICE_TABS.some(tab => tab.id === value);

const getSyncText = (status: SyncStatusDto): string =>
  [
    status.phase,
    `${status.instances ?? 0} instances`,
    `${status.definitions} definitions`,
    status.lastPollAt ? `polled ${formatTime(status.lastPollAt)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

const SyncStatus = () => {
  const client = useCoreServiceClient();
  const { data, error, reload } = useAsyncData(() => client.getSyncStatus(), [client]);
  const reloadRef = useRef(reload);

  reloadRef.current = reload;

  useEffect(() => {
    const timer = setInterval(() => reloadRef.current(), SYNC_STATUS_POLL_MS);

    return () => clearInterval(timer);
  }, []);

  if (!data) {
    return error ? <Chip size="small" color="error" variant="outlined" label="core unreachable" /> : null;
  }

  const isFailing = !!error || data.phase === 'FAILED' || !!data.lastError;
  const color = error
    ? 'error'
    : data.phase === 'READY' && !data.lastError
      ? 'success'
      : isFailing
        ? 'error'
        : 'warning';

  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      title={data.lastError || undefined}
      label={error ? 'core unreachable' : `${getSyncText(data)}${data.lastError ? ` · error: ${data.lastError}` : ''}`}
      sx={{ maxWidth: '100%' }}
    />
  );
};

const CoreServicePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTab = searchParams.get(CORE_SERVICE_TAB_PARAM);
  const storedTab = loadStoredValue(TAB_STORAGE_KEY);
  const activeTab = isCoreServiceTab(paramTab)
    ? paramTab
    : isCoreServiceTab(storedTab)
      ? storedTab
      : CoreServiceTab.TODAY;
  // Tabs keep their state (filters, expanded rows) after the first visit: a visited panel is only hidden.
  const [visited, setVisited] = useState<CoreServiceTab[]>([activeTab]);

  const changeTab = useCallback(
    (value: unknown) => {
      if (!isCoreServiceTab(value as string)) {
        return;
      }

      const next = value as CoreServiceTab;
      const params = new URLSearchParams(searchParams);

      params.set(CORE_SERVICE_TAB_PARAM, next);
      saveStoredValue(TAB_STORAGE_KEY, next);
      setVisited(previous => (previous.includes(next) ? previous : [...previous, next]));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const tabs = CORE_SERVICE_TABS.map(tab => ({ value: tab.id, label: tab.label }));

  return (
    <CoreEventsProvider>
      <Stack direction="column" gap={2} sx={{ minHeight: 'calc(100vh - 132px)' }}>
        <Stack direction="column" gap={1}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <SyncStatus />
          </Stack>
          <Typography variant="h1" color="brand" mb={1}>
            {CORE_SERVICE_TITLE}
          </Typography>
        </Stack>
        <Box>
          <TabsWrapper
            tabs={tabs}
            defaultTab={activeTab}
            isShowContent={false}
            changeTabCallback={changeTab as (index: number) => void}
          />
          <Box sx={{ paddingTop: 2 }}>
            {CORE_SERVICE_TABS.map(({ id }) => {
              const Content = TAB_COMPONENTS[id];

              return visited.includes(id) || id === activeTab ? (
                <Box key={id} hidden={id !== activeTab} sx={{ display: id === activeTab ? 'block' : 'none' }}>
                  <Content />
                </Box>
              ) : null;
            })}
          </Box>
        </Box>
      </Stack>
    </CoreEventsProvider>
  );
};

export default CoreServicePage;
